import type { Style } from "@bezpart-board/shared";

export type LineStrokeDash = "solid" | "dashed" | "dotted" | "wavy";

export type LineToolSettings = {
  stroke: string;
  strokeWidth: number;
  strokeLinecap: NonNullable<Style["strokeLinecap"]>;
  strokeDash?: LineStrokeDash;
};

const STORAGE_KEY = "wb-line-settings";

const DEFAULT_SETTINGS: LineToolSettings = {
  stroke: "#1a1a2e",
  strokeWidth: 3,
  strokeLinecap: "round",
  strokeDash: "solid",
};

function isDash(v: unknown): v is LineStrokeDash {
  return v === "solid" || v === "dashed" || v === "dotted" || v === "wavy";
}

function loadSettings(): LineToolSettings {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const p = JSON.parse(raw) as Partial<LineToolSettings>;
    return {
      stroke: typeof p.stroke === "string" ? p.stroke : DEFAULT_SETTINGS.stroke,
      strokeWidth:
        typeof p.strokeWidth === "number" && Number.isFinite(p.strokeWidth)
          ? p.strokeWidth
          : DEFAULT_SETTINGS.strokeWidth,
      strokeLinecap:
        p.strokeLinecap === "round" || p.strokeLinecap === "butt" || p.strokeLinecap === "square"
          ? p.strokeLinecap
          : DEFAULT_SETTINGS.strokeLinecap,
      strokeDash: isDash(p.strokeDash) ? p.strokeDash : DEFAULT_SETTINGS.strokeDash,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function persistSettings(next: LineToolSettings): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

let settings: LineToolSettings = loadSettings();

export function getLineSettings(): LineToolSettings {
  return settings;
}

export function setLineSettings(partial: Partial<LineToolSettings>): void {
  settings = { ...settings, ...partial };
  persistSettings(settings);
}

export const LINE_STROKE_PRESETS = [
  "#1a1a2e",
  "#0f172a",
  "#dc2626",
  "#2563eb",
  "#16a34a",
  "#7c3aed",
  "#ffffff",
] as const;

export const LINE_WIDTH_PRESETS = [1, 2, 3, 5, 8, 12, 16] as const;

export const LINE_DASH_PRESETS: { id: LineStrokeDash; label: string; title: string }[] = [
  { id: "solid", label: "─", title: "Сплошная" },
  { id: "dashed", label: "┄", title: "Пунктир" },
  { id: "dotted", label: "···", title: "Точки" },
  { id: "wavy", label: "∿", title: "Волнистая" },
];

export function lineSettingsEventName(): string {
  return "wb-line-settings";
}
